import { useEffect, useMemo, useState, startTransition } from 'react'
import { Maybe } from 'graphql/jsutils/Maybe'
import { ContentWithWatchableReasons, isStreaming } from '@/services/ProjectPhaseService'
import { Episode, getPreferredSeason, Project, ResumeWatchingData, Season } from '@/services/ProjectsService'
import { getSeasonByNameOrIndex } from '@/services/ProjectsService/ProjectsService'
import { normalizeProjectSeasons } from '@/services/ProjectsService/normalizeSeasons'
import { isARecap, isASneakPeek } from '@/utils/EpisodeUtil'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'

interface useWatchProjectDataProps {
  data: Project | undefined
  skip?: boolean
  isGuildMember?: boolean
  isGeoblockedProject?: boolean
  resumeWatchingData?: ResumeWatchingData
  /** Content with watchableReasons for streaming availability checks (from WatchProjectContext) */
  streamingContent?: ContentWithWatchableReasons | null
}

export const useWatchProjectData = ({
  data,
  skip,
  isGuildMember = false,
  isGeoblockedProject,
  resumeWatchingData,
  streamingContent,
}: useWatchProjectDataProps) => {
  const router = useSafeRouter()
  const seasonNameOrIndex = getItemOrFirstEntry(router.query.season)
  const project = useMemo(() => normalizeProjectSeasons(data), [data])
  // Use streamingContent from context if available, otherwise fall back to project.title
  const isTitleStreaming = isStreaming(streamingContent ?? project?.title)
  const seasons = project?.seasons?.filter(Boolean) as Season[] | undefined
  const [preferredSeason, setPreferredSeason] = useState<Season>()
  const watchableReasons = project?.title?.watchableReasons ?? []

  useEffect(() => {
    if (!project || !router.isReady) {
      return
    }

    startTransition(() => {
      if (seasonNameOrIndex) {
        setPreferredSeason(getSeasonByNameOrIndex({ seasonNameOrIndex, project }))
      } else {
        const preferredSeason = getPreferredSeason(project, resumeWatchingData)
        setPreferredSeason(preferredSeason)
      }
    })
  }, [data, project, router, seasonNameOrIndex, seasons, resumeWatchingData])

  let currentEpisode: Episode | undefined = undefined
  const lastWatched = resumeWatchingData?.watchableGuid

  if (lastWatched) {
    seasons?.forEach((season) => {
      season?.episodes?.forEach((episode) => {
        if (episode?.guid === lastWatched) {
          currentEpisode = episode
        }
      })
    })
  }

  if (!currentEpisode) {
    currentEpisode = returnDefaultEpisode({
      seasons,
      isGuildMember,
      project,
      isTitleStreaming,
      isGeoblockedProject,
      watchableReasons,
    })
  }

  return useMemo(() => {
    if (skip) {
      return {
        data,
      }
    }

    return {
      data,
      preferredSeason,
      currentEpisode,
    }
  }, [skip, data, preferredSeason, currentEpisode])
}

interface ReturnDefaultEpisodeParams {
  seasons: Season[] | undefined
  isGuildMember: boolean
  project: Maybe<Project>
  isTitleStreaming: boolean
  isGeoblockedProject?: boolean
  watchableReasons: Array<{
    __typename: string
    startDatetime?: string | null
  } | null> | null
}

function returnDefaultEpisode({
  seasons,
  isGuildMember,
  project,
  isTitleStreaming,
  isGeoblockedProject,
  watchableReasons,
}: ReturnDefaultEpisodeParams) {
  const recap =
    isGuildMember && project?.projectType === 'series'
      ? seasons?.[0]?.episodes?.find((episode) => isARecap(episode))
      : null
  const sneakPeek =
    !isGuildMember && project?.projectType === 'movie' && !isGeoblockedProject && !watchableReasons?.length
      ? seasons?.[0]?.episodes?.find((episode) => isASneakPeek(episode))
      : null
  const trailer =
    isGuildMember &&
    project?.projectType === 'movie' &&
    !isTitleStreaming &&
    !seasons?.[0]?.episodes?.find((episode) => episode?.name?.toLowerCase().includes('movie'))
      ? seasons?.[0]?.episodes?.find((episode) => episode?.isTrailer)
      : null
  // Show Trailer for guild members as default if movie isn't streaming yet and no episode is marked as the movie
  const movie =
    (isGuildMember || (!isGuildMember && watchableReasons?.length)) &&
    project?.projectType === 'movie' &&
    isTitleStreaming
      ? seasons?.[0]?.episodes?.find((episode) => episode?.name?.toLowerCase().includes('movie'))
      : null

  const firstWatchableEpisode = seasons?.[0]?.episodes?.find(
    (episode) => !episode?.unavailableReason && !isASneakPeek(episode),
  )
  // Account for if Project is a movie and media pipeline hasn't ordered them correctly
  return recap ?? sneakPeek ?? trailer ?? movie ?? firstWatchableEpisode ?? undefined
}
